import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { fmtRelativeTime } from "../lib/format";
import { Modal } from "./Modal";

type Via = "code" | "series number" | "set name" | "word" | "ai" | "default";
type Field<T> = { value: T; via: Via; matched?: string } | null;
type Classification = {
	pokemon: boolean;
	set: Field<{ id: string; name: string; seriesName: string }>;
	type: Field<{ id: string; label: string }>;
	language: Field<string>;
	quantity: Field<number>;
};
type AiResult =
	| {
			ok: true;
			model: string;
			elapsedMs: number;
			cost: number | null;
			answer: {
				pokemon: boolean;
				setId: string | null;
				typeId: string | null;
				language: string;
				quantity: number;
				certainty: "certain" | "likely" | "guess";
			};
	  }
	| { ok: false; error: string; elapsedMs: number };
type Listing = {
	productId: number;
	storeId: string;
	title: string;
	rules: Classification;
	ai: AiResult | null;
	final: Classification;
	key: string | null;
};
type Response = { listings: Listing[]; catalog: { sets: number; fetchedAt: number } };

const VIA_WORDS: Record<Via, string> = {
	code: "set code",
	"series number": "series and number",
	"set name": "set name",
	word: "word in title",
	ai: "AI",
	default: "assumed",
};

/**
 * What the catalogue classifier makes of one item's listings: each field
 * with how it was decided, the model's own answer beside it, and the key the
 * listing would group on. Nothing is saved; this is for judging the
 * classifier before anything relies on it.
 */
export function ClassifyModal({
	group,
	storeNames,
	onClose,
}: {
	group: { groupKey: string; title: string } | null;
	/** Store display names; loaded here when the page doesn't have them. */
	storeNames?: Map<string, string>;
	onClose: () => void;
}) {
	const [data, setData] = useState<Response | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loadedNames, setLoadedNames] = useState<Map<string, string> | null>(null);
	const names = storeNames ?? loadedNames ?? new Map<string, string>();

	useEffect(() => {
		if (!group || storeNames || loadedNames) return;
		fetch("/api/stores")
			.then((r) => r.json())
			.then((d: { stores: Array<{ id: string; name: string }> }) =>
				setLoadedNames(new Map(d.stores.map((s) => [s.id, s.name]))),
			)
			.catch(() => {});
	}, [group, storeNames, loadedNames]);

	useEffect(() => {
		if (!group) return;
		setData(null);
		setError(null);
		let cancelled = false;
		fetch("/api/classify/group", {
			method: "POST",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ groupKey: group.groupKey }),
		})
			.then(async (res) => {
				const body = (await res.json()) as Response & { error?: string };
				if (cancelled) return;
				if (!res.ok) setError(body.error ?? `HTTP ${res.status}`);
				else setData(body);
			})
			.catch((e: Error) => !cancelled && setError(e.message));
		return () => {
			cancelled = true;
		};
	}, [group]);

	const keys = new Set(data?.listings.map((l) => l.key));
	const agreement =
		data && data.listings.length > 1
			? keys.size === 1 && !keys.has(null)
				? { ok: true, text: `All ${data.listings.length} listings give the same key.` }
				: { ok: false, text: "The listings give different keys." }
			: null;

	return (
		<Modal
			open={group != null}
			onClose={onClose}
			title="Classification"
			subtitle={group?.title}
			footer={
				data && (
					<span className="mr-auto text-xs text-muted">
						Nothing is saved. Checked against {data.catalog.sets} official sets from TCGdex, updated{" "}
						{fmtRelativeTime(data.catalog.fetchedAt)}.
					</span>
				)
			}
		>
			<div className="min-h-0 flex-1 overflow-y-auto">
				{error && <p className="px-4 py-4 text-[13px] text-fault">{error}</p>}
				{!data && !error && (
					<p className="flex items-center gap-2 px-4 py-4 text-[13px] text-muted">
						<Loader2 className="size-4 animate-spin" aria-hidden /> Classifying…
					</p>
				)}
				{agreement && (
					<p className={`border-b border-hairline px-4 py-2 text-xs ${agreement.ok ? "text-live" : "text-needs"}`}>
						{agreement.text}
					</p>
				)}
				{data?.listings.map((l) => (
					<ListingResult key={l.productId} listing={l} store={names.get(l.storeId) ?? l.storeId} />
				))}
			</div>
		</Modal>
	);
}

function ListingResult({ listing: l, store }: { listing: Listing; store: string }) {
	const f = l.final;
	return (
		<section className="border-b border-hairline px-4 py-3 last:border-b-0">
			<p className="text-xs text-muted">{store}</p>
			<p className="text-[13px] font-medium text-ink">{l.title}</p>

			<dl className="mt-2 grid grid-cols-[88px_minmax(0,1fr)] gap-x-3 gap-y-1 text-[13px]">
				<Row label="Card product" value={f.pokemon ? "Yes" : "No"} />
				<Row
					label="Set"
					value={f.set ? `${f.set.value.name} (${f.set.value.id})` : "—"}
					sub={f.set && `${f.set.value.seriesName}, ${via(f.set)}`}
				/>
				<Row label="Type" value={f.type?.value.label ?? "—"} sub={f.type && via(f.type)} />
				<Row label="Language" value={f.language?.value ?? "—"} sub={f.language && via(f.language)} />
				<Row
					label="Quantity"
					value={String(f.quantity?.value ?? 1)}
					sub={f.quantity ? via(f.quantity) : "one unless stated"}
				/>
				<dt className="text-xs leading-6 text-muted">Key</dt>
				<dd className="font-mono text-xs leading-6 break-all text-ink">
					{l.key ?? <span className="font-sans text-muted">none, set or type missing</span>}
				</dd>
			</dl>

			<AiLine ai={l.ai} rules={l.rules} />
		</section>
	);
}

/** "by set code "me05"", or just "assumed" for a default. */
function via<T>(field: NonNullable<Field<T>>): string {
	if (field.via === "default") return "assumed";
	const word = VIA_WORDS[field.via];
	return `by ${field.matched ? `${word} "${field.matched}"` : word}`;
}

function Row({ label, value, sub }: { label: string; value: string; sub?: string | null }) {
	return (
		<>
			<dt className="text-xs leading-6 text-muted">{label}</dt>
			<dd className="min-w-0 leading-6 text-ink">
				{value}
				{sub && <span className="ml-2 text-xs text-muted">{sub}</span>}
			</dd>
		</>
	);
}

/** The model's own answer, and where it disagrees with the rules. */
function AiLine({ ai, rules }: { ai: AiResult | null; rules: Classification }) {
	if (!ai) return null;
	if (!ai.ok) {
		return <p className="mt-2 text-xs text-muted">AI not used: {ai.error}.</p>;
	}
	const a = ai.answer;
	const disagreements = [
		rules.set && a.setId !== rules.set.value.id && `set (${a.setId ?? "none"})`,
		rules.type && a.typeId !== rules.type.value.id && `type (${a.typeId ?? "other"})`,
	].filter(Boolean);
	return (
		<p className="mt-2 text-xs text-muted">
			AI ({ai.model}, {a.certainty}): set {a.setId ?? "none"}, {a.typeId ?? "other"}, {a.language}, ×{a.quantity}
			{!a.pokemon && ", not a card product"}. {(ai.elapsedMs / 1000).toFixed(1)}s
			{ai.cost != null && `, $${ai.cost.toFixed(5)}`}.
			{disagreements.length > 0 && (
				<span className="text-needs"> Disagrees with the rules on {disagreements.join(" and ")}; the rules win.</span>
			)}
		</p>
	);
}
